import { useState, useEffect, useCallback, useRef } from 'react';

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIBRARY_NAME = 'Family Vault';
const GRAPH = 'https://graph.microsoft.com/v1.0';

let cachedDriveId = null;

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg'].includes(ext)) return 'img';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  if (['xls', 'xlsx'].includes(ext)) return 'xls';
  return 'file';
}

function VaultPicker({ getToken, onPick, onCancel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ name: 'Family Vault', id: null }]);

  const resolveDrive = useCallback(async (token) => {
    if (cachedDriveId) return cachedDriveId;
    const siteRes = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!siteRes.ok) throw new Error(`Failed to resolve site (${siteRes.status})`);
    const site = await siteRes.json();

    const drivesRes = await fetch(`${GRAPH}/sites/${site.id}/drives?$select=name,id&$top=100`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!drivesRes.ok) throw new Error(`Failed to load drives (${drivesRes.status})`);
    const drivesData = await drivesRes.json();
    const drive = (drivesData.value || []).find(
      d => d.name.toLowerCase() === LIBRARY_NAME.toLowerCase()
    );
    if (!drive) throw new Error(`Library "${LIBRARY_NAME}" not found`);
    cachedDriveId = drive.id;
    return drive.id;
  }, []);

  const fetchItems = useCallback(async (fid) => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const driveId = await resolveDrive(token);
      const url = fid
        ? `${GRAPH}/drives/${driveId}/items/${fid}/children?$top=200`
        : `${GRAPH}/drives/${driveId}/root/children?$top=200`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load files (${res.status})`);
      const data = await res.json();
      const mapped = (data.value || []).map(item => ({
        id: item.id,
        name: item.name,
        isFolder: !!item.folder,
        size: item.size || 0,
        webUrl: item.webUrl || '',
        downloadUrl: item['@microsoft.graph.downloadUrl'] || '',
      }));
      // Sort: folders first, then files alphabetically
      mapped.sort((a, b) => {
        if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setItems(mapped);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, resolveDrive]);

  useEffect(() => { fetchItems(folderId); }, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  function navigateFolder(item) {
    setFolderId(item.id);
    setBreadcrumb(prev => [...prev, { name: item.name, id: item.id }]);
  }

  function navigateBreadcrumb(index) {
    const target = breadcrumb[index];
    setFolderId(target.id);
    setBreadcrumb(prev => prev.slice(0, index + 1));
  }

  function selectFile(item) {
    // Build a viewable URL — SharePoint online viewer for office docs, direct for others
    let viewUrl = item.webUrl || item.downloadUrl;
    onPick({ name: item.name, path: viewUrl, id: item.id });
  }

  return (
    <div className="VaultPicker-overlay" onClick={onCancel}>
      <div className="VaultPicker" onClick={e => e.stopPropagation()}>
        <div className="VaultPicker-header">
          <h3>Link a Vault Document</h3>
          <button className="RoleManager-close" onClick={onCancel}>&times;</button>
        </div>

        {/* Breadcrumb */}
        <div className="VaultPicker-breadcrumb">
          {breadcrumb.map((crumb, i) => (
            <span key={i}>
              {i > 0 && <span className="VaultPicker-breadcrumb-sep">/</span>}
              <button
                className={`VaultPicker-breadcrumb-btn${i === breadcrumb.length - 1 ? ' VaultPicker-breadcrumb-btn--current' : ''}`}
                onClick={() => navigateBreadcrumb(i)}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {error && <p className="Fitness-error">{error}</p>}
        {loading && <p className="Centcom-events-loading">Loading...</p>}

        {!loading && (
          <div className="VaultPicker-list">
            {items.length === 0 && <p className="Centcom-events-empty">Empty folder</p>}
            {items.map(item => (
              <button
                key={item.id}
                className="VaultPicker-item"
                onClick={() => item.isFolder ? navigateFolder(item) : selectFile(item)}
              >
                <span className={`VaultPicker-icon VaultPicker-icon--${item.isFolder ? 'folder' : fileIcon(item.name)}`}>
                  {item.isFolder ? '\uD83D\uDCC1' : '\uD83D\uDCC4'}
                </span>
                <span className="VaultPicker-item-name">{item.name}</span>
                {!item.isFolder && <span className="VaultPicker-item-pick">Select</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VaultPicker;
